/* Security dashboard — in-app firewall status, scored events, blocked IPs,
   and firewall settings. Admin-only (gated on the `security` RBAC page).
   Data is loaded on demand (not in the global bootstrap). */

const { useState: useStateSec, useEffect: useEffectSec } = React;

const SEV_COLOR = { critical: 'var(--neg)', high: 'var(--neg)', medium: 'var(--warn, #b8860b)', low: 'var(--ink-3)' };
const ACTION_COLOR = { banned: 'var(--neg)', block: 'var(--neg)', 'ip-ban': 'var(--neg)', 'geo-block': 'var(--neg)', 'would-block': 'var(--warn, #b8860b)', log: 'var(--ink-3)' };

function SecTag({ text, color }) {
  return <span className="tag" style={{ color, borderColor: color, fontFamily: 'var(--font-mono)', fontSize: 10, textTransform: 'uppercase' }}>{text}</span>;
}

function SecurityPage() {
  const [overview, setOverview] = useStateSec(null);
  const [events, setEvents] = useStateSec([]);
  const [bans, setBans] = useStateSec([]);
  const [settings, setSettings] = useStateSec(null);
  const [sevFilter, setSevFilter] = useStateSec('all');
  const [actionFilter, setActionFilter] = useStateSec('all');
  const [q, setQ] = useStateSec('');
  const [banIp, setBanIp] = useStateSec('');
  const [banReason, setBanReason] = useStateSec('');
  const [banMinutes, setBanMinutes] = useStateSec('');
  const [err, setErr] = useStateSec('');
  const [saving, setSaving] = useStateSec(false);
  const canEdit = window.can && window.can('security', 'edit');

  const loadOverview = async () => { try { setOverview(await window.QH_API.get('/security/overview')); } catch {} };
  const loadBans = async () => { try { setBans(await window.QH_API.get('/security/bans')); } catch {} };
  const loadSettings = async () => { try { setSettings(await window.QH_API.get('/security/settings')); } catch {} };
  const loadEvents = async () => {
    const params = new URLSearchParams({ pageSize: '150' });
    if (sevFilter !== 'all') params.set('severity', sevFilter);
    if (actionFilter !== 'all') params.set('action', actionFilter);
    if (q.trim()) params.set('q', q.trim());
    try { const r = await window.QH_API.get('/security/events?' + params.toString()); setEvents(r.items || []); } catch {}
  };

  useEffectSec(() => { loadOverview(); loadBans(); loadSettings(); /* eslint-disable-next-line */ }, []);
  useEffectSec(() => { loadEvents(); /* eslint-disable-next-line */ }, [sevFilter, actionFilter]);

  const refreshAll = () => { loadOverview(); loadEvents(); loadBans(); };

  const submitBan = async (e) => {
    e?.preventDefault();
    setErr('');
    if (!banIp.trim()) { setErr('Enter an IP address to ban.'); return; }
    try {
      await window.QH_API.post('/security/bans', {
        ip: banIp.trim(),
        reason: banReason.trim() || null,
        minutes: banMinutes.trim() ? parseInt(banMinutes, 10) : null,
      });
      setBanIp(''); setBanReason(''); setBanMinutes('');
      refreshAll();
    } catch (e2) { setErr(e2.message || 'Ban failed.'); }
  };

  const unban = async (id) => {
    try { await window.QH_API.delete(`/security/bans/${id}`); refreshAll(); } catch (e2) { setErr(e2.message); }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setErr(''); setSaving(true);
    try {
      await window.QH_API.put('/security/settings', {
        enabled: !!settings.enabled,
        blockEnabled: !!settings.blockEnabled,
        blockThreshold: parseInt(settings.blockThreshold, 10) || 50,
        logThreshold: parseInt(settings.logThreshold, 10) || 10,
        autoBanMinutes: parseInt(settings.autoBanMinutes, 10) || 60,
        eventRetentionDays: parseInt(settings.eventRetentionDays, 10) || 30,
        geoMode: settings.geoMode || 'disabled',
        geoCountries: settings.geoCountries || '',
        geoAllowWhenUnknown: !!settings.geoAllowWhenUnknown,
        geoMmdbPath: settings.geoMmdbPath || '',
      });
      await loadSettings(); await loadOverview();
    } catch (e2) { setErr(e2.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const setS = (k) => (e) => setSettings(s => ({ ...s, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const mode = overview?.firewall?.mode || 'off';
  const modeColor = mode === 'blocking' ? 'var(--neg)' : mode === 'monitor-only' ? 'var(--warn, #b8860b)' : 'var(--ink-3)';
  const st = overview?.stats || {};

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>System</span><I.ChevronRight s={10}/><span>Security</span></div>
          <h1>Security</h1>
          <div className="subtitle">In-app request firewall. Malicious requests are scored, logged, and (when blocking is on) denied.</div>
        </div>
        <div className="page-header-actions">
          <span className="tag" style={{ color: modeColor, borderColor: modeColor }}>
            <I.Lock s={11}/> Firewall: {mode}
          </span>
          <button className="btn" onClick={refreshAll}><I.Filter/> Refresh</button>
        </div>
      </div>

      <div className="page-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {err && <div className="text-xs" style={{ color: 'var(--neg)' }}><I.AlertCircle s={12}/> {err}</div>}
        {mode === 'monitor-only' && (
          <div className="card" style={{ padding: '10px 14px', fontSize: 12, color: 'var(--ink-2)', borderLeft: '3px solid var(--warn, #b8860b)' }}>
            <I.AlertCircle s={12}/> Monitor-only mode: attacks are logged but not blocked. Review the events below, then enable blocking in Settings once the log looks clean.
          </div>
        )}

        <KPIStrip items={[
          { label: 'Requests scored (24h)', value: String(st.scored24 ?? 0) },
          { label: 'Blocked (24h)', value: String(st.blocked24 ?? 0) },
          { label: 'Blocked IPs', value: String(st.blockedIps ?? 0) },
          { label: 'Active bans', value: String(st.activeBans ?? 0) },
        ]}/>

        {/* Top offending IPs */}
        <div className="card" style={{ padding: 0 }}>
          <div className="card-header">Top offending IPs · last 24h</div>
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>IP</th><th>Events</th><th>Max score</th><th>Top rule</th><th>Last seen</th></tr></thead>
              <tbody>
                {(overview?.topOffenders || []).map((o, i) => (
                  <tr key={i}>
                    <td className="font-mono text-xs">{o.ip}</td>
                    <td>{o.events}</td>
                    <td className="font-mono text-xs">{o.maxScore}</td>
                    <td className="font-mono text-xs muted">{o.topRule || '—'}</td>
                    <td className="font-mono text-xs muted">{window.fmtDateTime(o.lastSeen)}</td>
                  </tr>
                ))}
                {(!overview?.topOffenders || overview.topOffenders.length === 0) && (
                  <tr><td colSpan={5} className="text-xs muted" style={{ padding: 16 }}>No scored requests in the last 24 hours.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent events */}
        <div className="card" style={{ padding: 0 }}>
          <div className="card-header">Recent events</div>
          <div className="toolbar" style={{ border: 'none' }}>
            {[['all', 'All'], ['critical', 'Critical'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']].map(([k, l]) => (
              <button key={k} onClick={() => setSevFilter(k)} className={`btn sm ${sevFilter === k ? 'accent' : 'ghost'}`}>{l}</button>
            ))}
            <div className="sep"/>
            {[['all', 'Any action'], ['would-block', 'Would-block'], ['block', 'Blocked'], ['banned', 'Auto-banned'], ['ip-ban', 'IP-ban'], ['geo-block', 'Geo-block'], ['log', 'Logged']].map(([k, l]) => (
              <button key={k} onClick={() => setActionFilter(k)} className={`btn sm ${actionFilter === k ? 'accent' : 'ghost'}`}>{l}</button>
            ))}
            <div className="grow"/>
            <input className="input" style={{ height: 28, width: 220 }} placeholder="Search path / rule / IP…" value={q}
                   onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadEvents()}/>
            <button className="btn sm" onClick={loadEvents}>Search</button>
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Timestamp</th><th>IP</th><th>Method</th><th>Path</th><th>Score</th><th>Severity</th><th>Action</th><th>Rules</th></tr></thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td className="font-mono text-xs muted">{window.fmtDateTime(e.createdAt)}</td>
                    <td className="font-mono text-xs">{e.ip || '—'}</td>
                    <td className="font-mono text-xs">{e.method}</td>
                    <td className="font-mono text-xs" style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.path + (e.queryExcerpt || '')}>{e.path}{e.queryExcerpt}</td>
                    <td className="font-mono text-xs">{e.score}</td>
                    <td><SecTag text={e.severity} color={SEV_COLOR[e.severity] || 'var(--ink-3)'}/></td>
                    <td><SecTag text={e.action} color={ACTION_COLOR[e.action] || 'var(--ink-3)'}/></td>
                    <td className="font-mono text-xs muted" style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.ruleKeys}>{e.ruleKeys}</td>
                  </tr>
                ))}
                {events.length === 0 && <tr><td colSpan={8} className="text-xs muted" style={{ padding: 16 }}>No events match the current filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Blocked IPs */}
        <div className="card" style={{ padding: 0 }}>
          <div className="card-header">Blocked IPs</div>
          {canEdit && (
            <form className="toolbar" style={{ border: 'none', gap: 8 }} onSubmit={submitBan}>
              <input className="input" style={{ height: 28, width: 160 }} placeholder="IP address" value={banIp} onChange={(e) => setBanIp(e.target.value)}/>
              <input className="input" style={{ height: 28, width: 240 }} placeholder="Reason (optional)" value={banReason} onChange={(e) => setBanReason(e.target.value)}/>
              <input className="input" style={{ height: 28, width: 150 }} placeholder="Minutes (blank = permanent)" value={banMinutes} onChange={(e) => setBanMinutes(e.target.value)}/>
              <button className="btn primary sm" type="submit"><I.Plus/> Ban IP</button>
            </form>
          )}
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>IP</th><th>Reason</th><th>Source</th><th>Banned</th><th>Expires</th><th>Status</th>{canEdit && <th></th>}</tr></thead>
              <tbody>
                {bans.map((b) => (
                  <tr key={b.id}>
                    <td className="font-mono text-xs">{b.ip}</td>
                    <td className="text-xs muted" style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.reason}>{b.reason || '—'}</td>
                    <td><span className="tag">{b.source}</span></td>
                    <td className="font-mono text-xs muted">{window.fmtDateTime(b.blockedAt)}</td>
                    <td className="font-mono text-xs muted">{b.expiresAt ? window.fmtDateTime(b.expiresAt) : 'permanent'}</td>
                    <td><SecTag text={b.active ? 'active' : 'expired'} color={b.active ? 'var(--neg)' : 'var(--ink-3)'}/></td>
                    {canEdit && <td><button className="btn sm ghost" onClick={() => unban(b.id)}>Unban</button></td>}
                  </tr>
                ))}
                {bans.length === 0 && <tr><td colSpan={canEdit ? 7 : 6} className="text-xs muted" style={{ padding: 16 }}>No blocked IPs.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Settings */}
        {settings && (
          <div className="card" style={{ padding: 16 }}>
            <div className="card-header" style={{ padding: 0, marginBottom: 12 }}>Firewall settings</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={!!settings.enabled} onChange={setS('enabled')} disabled={!canEdit}/>
                Firewall enabled
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={!!settings.blockEnabled} onChange={setS('blockEnabled')} disabled={!canEdit}/>
                Active blocking (off = monitor-only)
              </label>
              <label className="text-xs" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                Block threshold
                <input className="input" style={{ height: 28 }} value={settings.blockThreshold} onChange={setS('blockThreshold')} disabled={!canEdit}/>
              </label>
              <label className="text-xs" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                Log threshold
                <input className="input" style={{ height: 28 }} value={settings.logThreshold} onChange={setS('logThreshold')} disabled={!canEdit}/>
              </label>
              <label className="text-xs" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                Auto-ban minutes
                <input className="input" style={{ height: 28 }} value={settings.autoBanMinutes} onChange={setS('autoBanMinutes')} disabled={!canEdit}/>
              </label>
              <label className="text-xs" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                Event retention (days)
                <input className="input" style={{ height: 28 }} value={settings.eventRetentionDays} onChange={setS('eventRetentionDays')} disabled={!canEdit}/>
              </label>
            </div>

            {/* Geo-gating */}
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>Geo-gating</span>
                <SecTag text={settings.geoDbLoaded ? 'DB loaded' : 'no DB'} color={settings.geoDbLoaded ? 'var(--pos, #1a7f37)' : 'var(--ink-3)'}/>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                <label className="text-xs" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  Mode
                  <select className="input" style={{ height: 28 }} value={settings.geoMode || 'disabled'} onChange={setS('geoMode')} disabled={!canEdit}>
                    <option value="disabled">Disabled</option>
                    <option value="allowlist">Allowlist — only these pass</option>
                    <option value="blocklist">Blocklist — these denied</option>
                  </select>
                </label>
                <label className="text-xs" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  Countries (ISO codes, comma-separated)
                  <input className="input" style={{ height: 28 }} placeholder="BH, SA, AE, KW, IN" value={settings.geoCountries || ''} onChange={setS('geoCountries')} disabled={!canEdit}/>
                </label>
                <label className="text-xs" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  Country .mmdb path (on the server)
                  <input className="input" style={{ height: 28 }} placeholder="/var/qhms/geo/country.mmdb" value={settings.geoMmdbPath || ''} onChange={setS('geoMmdbPath')} disabled={!canEdit}/>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, alignSelf: 'flex-end', paddingBottom: 6 }}>
                  <input type="checkbox" checked={!!settings.geoAllowWhenUnknown} onChange={setS('geoAllowWhenUnknown')} disabled={!canEdit}/>
                  Allow when country unknown
                </label>
              </div>
              {settings.geoMode !== 'disabled' && !settings.geoDbLoaded && (
                <div className="text-xs" style={{ color: 'var(--warn, #b8860b)', marginTop: 8 }}>
                  <I.AlertCircle s={11}/> No MMDB loaded at that path — geo-gating reads every request as "unknown". Supply a free GeoLite2-Country or DB-IP Lite Country <b>.mmdb</b> file and set its server path above.
                </div>
              )}
              <div className="text-xs muted" style={{ marginTop: 8 }}>
                Auth and this Security page are always exempt from geo-gating, so an admin abroad can still sign in.
              </div>
            </div>

            {canEdit && (
              <div style={{ marginTop: 14 }}>
                <button className="btn primary" onClick={saveSettings} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
                <span className="text-xs muted" style={{ marginLeft: 10 }}>Log threshold must be below block threshold.</span>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

window.SecurityPage = SecurityPage;
